/**
 * Dark Pool Auction System
 * Manages round lifecycle: idle -> betting -> reveal -> result -> idle
 * Players bet real coins. Winnings = bet * multiplier.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { DARKPOOL_CONFIG, pickRoundCoins, resolveRound, getRewardMultiplier } from '../config/darkpool.js';

export const darkPoolSystem: TickSystem<GameState, GameAction> = {
  name: 'darkPool',

  update(state) {
    if (!state.isRunning) return state;
    if (!state.darkPool) return state;

    const now = Date.now();
    const dp = state.darkPool;

    // === IDLE: Start new round when timer expires ===
    if (dp.phase === 'idle') {
      if (now >= dp.nextRoundTime) {
        const roundCoins = pickRoundCoins(DARKPOOL_CONFIG.coinsPerRound);
        const coinIds = roundCoins.map(c => c.id);
        const outcome = resolveRound(coinIds, state.purchasedUpgrades);

        return {
          ...state,
          darkPool: {
            ...dp,
            phase: 'betting',
            coins: roundCoins.map(c => ({ id: c.id, name: c.name, icon: c.icon })),
            playerBet: null,
            insiderInfo: null,
            result: null,
            _outcome: outcome,
            phaseEndTime: now + DARKPOOL_CONFIG.bettingDuration,
          },
        };
      }
      return state;
    }

    // === BETTING: Transition to reveal when time expires ===
    if (dp.phase === 'betting') {
      if (now >= dp.phaseEndTime) {
        return {
          ...state,
          darkPool: {
            ...dp,
            phase: 'reveal',
            phaseEndTime: now + DARKPOOL_CONFIG.revealDuration,
          },
        };
      }
      return state;
    }

    // === REVEAL: Show result after dramatic pause ===
    if (dp.phase === 'reveal') {
      if (now >= dp.phaseEndTime) {
        return {
          ...state,
          darkPool: {
            ...dp,
            phase: 'result',
            result: dp._outcome,
            phaseEndTime: now + DARKPOOL_CONFIG.resultDuration,
          },
        };
      }
      return state;
    }

    // === RESULT: Return to idle after display time ===
    if (dp.phase === 'result') {
      if (now >= dp.phaseEndTime) {
        // Auto-claim if player had a bet (unclaimed)
        let coins = state.coins;
        let totalProfit = dp.totalProfit;
        let totalWins = dp.totalWins;
        let totalEarned = state.statistics.totalEarned;

        if (dp.playerBet && dp.result) {
          const won = dp.playerBet.coinId === dp.result.winnerId;
          if (won) {
            const reward = dp.playerBet.amount * dp.result.multiplier * getRewardMultiplier(state.purchasedUpgrades);
            coins += reward;
            totalProfit += reward - dp.playerBet.amount;
            totalWins += 1;
            totalEarned += reward;
          } else {
            totalProfit -= dp.playerBet.amount;
          }
        }

        return {
          ...state,
          coins,
          statistics: { ...state.statistics, totalEarned },
          darkPool: {
            ...dp,
            phase: 'idle',
            coins: [],
            playerBet: null,
            insiderInfo: null,
            result: null,
            _outcome: null,
            phaseEndTime: 0,
            nextRoundTime: now + DARKPOOL_CONFIG.roundCooldown,
            totalProfit,
            totalWins,
          },
        };
      }
      return state;
    }

    return state;
  },
};
